import { Autocomplete, Box, FormControl, TextField, Typography } from "@mui/material";
import { FormikErrors, FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import {
	FreelancerOnboardingDataProcessStageViewModel,
	FreelancerOnboardingMySkillsStepViewModel,
	FreelancerOnboardingSkillViewModel,
	FreelancerOnboardingToolViewModel
} from "../../../../../ViewModels/FreelancerOnboardingMySkillsStepViewModel";
import { DataProcessStage } from "../../../../../models/DataProcessStage";
import { FreelancerProfileTool } from "../../../../../models/FreelancerProfileTool";
import { SkillLevel } from "../../../../../models/enums/SkillLevel";
import { ToolLevel } from "../../../../../models/enums/ToolLevel";
import { zoullooColors } from "../../../../../theme";
import { FreelancerProfileSkill } from "../../../../../models/FreelancerProfileSkill";

const FreelancerOnboardingMySkillsDataProcessStagesBloc = ({
	dataProcessStage,
	dataProcessStageIndex,
	formik
}: {
	dataProcessStage: DataProcessStage;
	dataProcessStageIndex: number;
	formik: FormikProps<FreelancerOnboardingMySkillsStepViewModel>;
}) => {
	const { t } = useTranslation();
	const getFreelancerProfileToolId = (
		freelancerProfileTools: FreelancerProfileTool[],
		toolId: number
	) => freelancerProfileTools.find(x => x.tool.id === toolId)?.id;
	const getFreelancerProfileSkillId = (
		freelancerProfileSkills: FreelancerProfileSkill[],
		skillId: number
	) => freelancerProfileSkills.find(x => x.skill.id === skillId)?.id;

	return (
		<Box sx={{ mb: 4, width: "41%" }} key={`dataProcessStage_${dataProcessStage.id}`}>
			<Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
				{t(`DataProcessStages.${dataProcessStage.label}`)}
			</Typography>
			{dataProcessStage.skills.length > 0 &&
				[
					{
						translationKey:
							dataProcessStage.label === "Teamwork"
								? "selectVeryGoodTeamworkSkills"
								: "selectVeryGoodSkills",
						level: SkillLevel.VeryGood
					},
					{
						translationKey:
							dataProcessStage.label === "Teamwork"
								? "selectGoodTeamworkSkills"
								: "selectGoodSkills",
						level: SkillLevel.Good
					}
				].map(skillBloc => (
					<Box
						sx={{ mb: 2, width: "100%" }}
						key={`skill_${skillBloc.translationKey}_${skillBloc.level}`}
					>
						<Typography variant="body2" gutterBottom>
							{t(`FreelancerOnboardingMySkills.skills.${skillBloc.translationKey}`)}
						</Typography>
						<FormControl fullWidth size="small">
							<Autocomplete
								multiple={true}
								disableCloseOnSelect={true}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								getOptionLabel={e => t(`Skills.${e.label}`)}
								onBlur={() => {
									formik.setFieldTouched(
										`dataProcessStageViewModels.${dataProcessStageIndex}.id`,
										true
									);
								}}
								options={dataProcessStage.skills.filter(
									x =>
										!formik.values.dataProcessStageViewModels
											.find(x => x.id === dataProcessStage.id)
											?.skillViewModels.some(
												y =>
													y.skill.id === x.id &&
													y.dataProcessStage.id === dataProcessStage.id &&
													y.level ===
														(skillBloc.level === SkillLevel.VeryGood
															? SkillLevel.Good
															: SkillLevel.VeryGood)
											)
								)}
								groupBy={option => t(`CapabilityCategories.${option.capabilityCategory}`)}
								value={
									formik.values.dataProcessStageViewModels
										.find(x => x.id === dataProcessStage.id)
										?.skillViewModels.filter(
											x =>
												x.dataProcessStage.id === dataProcessStage.id &&
												x.level === skillBloc.level
										)
										.map(x => x.skill) ?? []
								}
								onChange={(_, newValue) => {
									formik.setFieldValue(
										`dataProcessStageViewModels.${dataProcessStageIndex}.skillViewModels`,
										[
											...(formik.values.dataProcessStageViewModels
												.find(x => x.id === dataProcessStage.id)
												?.skillViewModels.filter(
													x =>
														x.dataProcessStage.id !== dataProcessStage.id ||
														x.level !== skillBloc.level
												) ?? []),
											...newValue.map(
												skill =>
													({
														id: getFreelancerProfileSkillId(
															formik.values.dataProcessStageViewModels[
																dataProcessStageIndex
															]?.skillViewModels ?? [],
															skill.id
														),
														skill,
														dataProcessStage,
														level: skillBloc.level
													} as FreelancerOnboardingSkillViewModel)
											)
										]
									);
								}}
								renderInput={params => (
									<TextField
										{...params}
										placeholder={t("FreelancerOnboardingMySkills.skills.placeholder")}
										variant="outlined"
										sx={{
											width: "100%"
										}}
									/>
								)}
								sx={{ mb: 2 }}
							/>
						</FormControl>
					</Box>
				))}

			{dataProcessStage.tools.length > 0 &&
				[
					{
						translationKey: "selectMasteredTools",
						level: ToolLevel.Mastered
					},
					{
						translationKey: "selectUsedTools",
						level: ToolLevel.AlreadyUsed
					}
				].map(toolBloc => (
					<Box sx={{ mb: 2 }} key={`tool_${toolBloc.translationKey}_${toolBloc.level}`}>
						<Typography variant="body2" gutterBottom>
							{t(`FreelancerOnboardingMySkills.skills.${toolBloc.translationKey}`)}
						</Typography>
						<FormControl fullWidth size="small">
							<Autocomplete
								multiple={true}
								disableCloseOnSelect={true}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								options={dataProcessStage.tools.filter(
									x =>
										!formik.values.dataProcessStageViewModels
											.find(x => x.id === dataProcessStage.id)
											?.toolViewModels.some(
												y =>
													y.tool.id === x.id &&
													y.dataProcessStage.id === dataProcessStage.id &&
													y.level ===
														(toolBloc.level === ToolLevel.Mastered
															? ToolLevel.AlreadyUsed
															: ToolLevel.Mastered)
											)
								)}
								groupBy={option => t(`CapabilityCategories.${option.capabilityCategory}`)}
								value={
									formik.values.dataProcessStageViewModels
										.find(x => x.id === dataProcessStage.id)
										?.toolViewModels.filter(
											x =>
												x.dataProcessStage.id === dataProcessStage.id &&
												x.level === toolBloc.level
										)
										.map(x => x.tool) ?? []
								}
								onChange={(_, newValue) => {
									formik.setFieldValue(
										`dataProcessStageViewModels.${dataProcessStageIndex}.toolViewModels`,
										[
											...(formik.values.dataProcessStageViewModels[
												dataProcessStageIndex
											]?.toolViewModels.filter(
												x =>
													x.dataProcessStage.id !== dataProcessStage.id ||
													x.level !== toolBloc.level
											) ?? []),
											...newValue.map(
												tool =>
													({
														id: getFreelancerProfileToolId(
															formik.values.dataProcessStageViewModels[
																dataProcessStageIndex
															]?.toolViewModels ?? [],
															tool.id
														),
														tool,
														dataProcessStage,
														level: toolBloc.level
													} as FreelancerOnboardingToolViewModel)
											)
										]
									);
								}}
								onBlur={() => {
									formik.setFieldTouched(
										`dataProcessStageViewModels.${dataProcessStageIndex}.id`,
										true
									);
								}}
								renderInput={params => (
									<TextField
										{...params}
										placeholder={t("FreelancerOnboardingMySkills.skills.placeholder")}
										variant="outlined"
										sx={{ width: "100%" }}
									/>
								)}
								sx={{ mb: 2 }}
							/>
						</FormControl>
					</Box>
				))}
			<Typography marginTop={2} sx={{ color: `${zoullooColors.error.main} !important` }}>
				{formik.touched.dataProcessStageViewModels?.[dataProcessStageIndex]?.id &&
					((
						formik.errors
							.dataProcessStageViewModels as FormikErrors<FreelancerOnboardingDataProcessStageViewModel>[]
					)?.[dataProcessStageIndex]?.skillViewModels as string)}
			</Typography>
			<Typography marginTop={2} sx={{ color: `${zoullooColors.error.main} !important` }}>
				{formik.touched.dataProcessStageViewModels?.[dataProcessStageIndex]?.id &&
					((
						formik.errors
							.dataProcessStageViewModels as FormikErrors<FreelancerOnboardingDataProcessStageViewModel>[]
					)?.[dataProcessStageIndex]?.toolViewModels as string)}
			</Typography>
		</Box>
	);
};

export default FreelancerOnboardingMySkillsDataProcessStagesBloc;
